import { hasOwnProperty, definedMap, withDefault, setUnion } from "collection-utils";

import type { JSONData } from "../Support";
import {
    assert,
    assertNever,
    panic,
    checkString,
    checkBoolean,
    defined,
    checkArray,
    definedMapWithDefault,
    isUndefinedish,
    mapFilterUndefined,
} from "../Support";
import type { DeserializationSpec, ArgumentKind, ArgumentSpec, DeserializableClass } from "./computation-types";
import { RootEvalEnvironment, Callable } from "./computation-types";
import { makeValueFromJSON, Data, Constant } from "./data";
import type {
    SerializedApp,
    SerializedComponent,
    SerializedMenuItem,
    SerializedScreen,
    SerializedRootScreen,
} from "./SerializedApp";
import { ScreenFlag } from "./SerializedApp";
import type { GlideFunctionalRenderer, ReactRenderer } from "./uis";
import { registerReactRenderer } from "./uis";
import type { ScreenDescription } from "../description";
import { getTableRefTableName } from "../description";
import type {
    Evaluable,
    EnvironmentBoundEvaluable,
    Action,
    UIClass,
    ActionClass,
    Context,
    ValueProducer,
    EvalEnvironment,
    AppEnvironment,
    AppOther,
    EvaluatedScreen,
    EvaluatedMenuItem,
    UI,
} from "./types";
import { UIEvaluable, ActionEvaluable, asAction } from "./types";
import { asCapturedEvalEnvironment } from "./primitives";
import { logInfo } from "../debug-print";

interface ActionDeserializableClass {
    new (...args: EnvironmentBoundEvaluable[]): Action;
    spec: DeserializationSpec<"action">;
}

const registeredDeserializables = new Map<string, DeserializableClass<AppOther, AppEnvironment>>();
const registeredUIDeserializables = new Map<string, UIClass>();
const registeredActionDeserializables = new Map<string, ActionDeserializableClass>();

function isDeserializableRegistered(name: string): boolean {
    return (
        registeredDeserializables.has(name) ||
        registeredUIDeserializables.has(name) ||
        registeredActionDeserializables.has(name)
    );
}

export function registerDeserializable(d: DeserializableClass<AppOther, AppEnvironment>): void {
    const [name] = d.spec;
    if (isDeserializableRegistered(name)) {
        // This happens with formulas compiled to JS when we reload
        // the app.
        logInfo("Re-registering deserializable", name);
    }
    registeredDeserializables.set(name, d);
}

export function registerUIDeserializable(renderer: ReactRenderer): void;
export function registerUIDeserializable<T extends UI = UI>(
    d: UIClass,
    renderer: ReactRenderer | GlideFunctionalRenderer<T>
): void;
export function registerUIDeserializable(
    d: UIClass | ReactRenderer,
    renderer?: ReactRenderer | GlideFunctionalRenderer
): void {
    let ui: UIClass;
    if (renderer === undefined) {
        renderer = d as ReactRenderer;
        ui = defined(renderer.ui);
    } else {
        ui = d as UIClass;
    }
    const [name] = ui.spec;
    assert(!isDeserializableRegistered(name), `${name} registered more than once`);
    registeredUIDeserializables.set(name, ui);
    registerReactRenderer(name, renderer);
}

export function registerActionDeserializable(d: ActionDeserializableClass): void {
    const [name] = d.spec;
    assert(!isDeserializableRegistered(name), `${name} registered more than once`);
    registeredActionDeserializables.set(name, d);
}

export interface DecodedArgumentSpec {
    readonly argName: string;
    readonly argKind: ArgumentKind;
    readonly required: boolean;
}

export function decodeArgumentSpec(argSpec: ArgumentSpec): DecodedArgumentSpec {
    if (typeof argSpec === "string") {
        return { argName: argSpec, argKind: "evaluable", required: true };
    } else {
        const rest = argSpec.slice(2);
        return {
            argName: argSpec[0],
            argKind: argSpec[1],
            required: rest.length === 0 || rest[0] !== false,
        };
    }
}

export function deserializeEvaluable(rootJSON: JSONData): Evaluable {
    function deserializeArgument(kind: ArgumentKind, json: JSONData): unknown {
        // logInfo(`deserializing ${kind} in ${JSON.stringify(json)}`);
        switch (kind) {
            case "evaluable":
            case "callable":
            case "ui":
            case "action":
                return deserialize(json);
            case "boolean":
                return checkBoolean(json);
            case "string":
                return checkString(json);
            case "json":
                return makeValueFromJSON(json, false);
            case "constant-json":
                return makeValueFromJSON(json, true);
            case "evaluable-array":
                if (!Array.isArray(json)) return panic();
                return json.map(deserialize);
            case "bindings":
                if (!Array.isArray(json)) return panic();
                return json.map(kvp => {
                    if (!Array.isArray(kvp)) return panic();
                    if (kvp.length !== 2) return panic();
                    return [checkString(kvp[0]), deserialize(kvp[1])];
                });
            case "string-array":
                if (!Array.isArray(json)) return panic();
                return json.map(checkString);
            case "kvp-array":
                if (!Array.isArray(json)) return panic();
                return json.map(kvp => {
                    if (!Array.isArray(kvp)) return panic();
                    if (kvp.length !== 2) return panic();
                    return kvp.map(deserialize);
                });
            case "string-json-array":
                if (!Array.isArray(json)) return panic();
                return json.map(pair => {
                    if (!Array.isArray(pair)) return panic();
                    if (pair.length !== 2) return panic();
                    return [checkString(pair[0]), pair[1]];
                });
            default:
                return assertNever(kind);
        }
    }

    function deserialize(json: JSONData): Evaluable {
        if (!hasOwnProperty(json, "$kind")) {
            return panic(`No $kind field in JSON ${json}`);
        }
        const kind = json.$kind;
        if (typeof kind !== "string") {
            return panic(`$kind is not a string in JSON ${json}`);
        }

        let argsSpec: ArgumentSpec[];
        let build: (args: unknown[]) => Evaluable;

        const deserializable = registeredDeserializables.get(kind);
        if (deserializable !== undefined) {
            [, , ...argsSpec] = deserializable.spec;
            build = a => new deserializable(...a);
        } else {
            const uiDeserializable = registeredUIDeserializables.get(kind);
            if (uiDeserializable !== undefined) {
                [, , ...argsSpec] = uiDeserializable.spec;
                build = a => new UIEvaluable(uiDeserializable as UIClass, a as any[]);
            } else {
                const actionDeserializable = registeredActionDeserializables.get(kind);
                if (actionDeserializable !== undefined) {
                    [, , ...argsSpec] = actionDeserializable.spec;
                    build = a => new ActionEvaluable(actionDeserializable as ActionClass, a as any[]);
                } else {
                    return panic(`Deserializable ${kind} not found`);
                }
            }
        }

        const args = argsSpec.map(argSpec => {
            const { argName, argKind, required } = decodeArgumentSpec(argSpec);
            const arg = (json as any)[argName];
            if (arg === undefined) {
                if (required) {
                    return panic(`Argument ${argName} missing from component: ${JSON.stringify(json)}`);
                }
                return undefined;
            }
            // logInfo(`deserializing ${kind} ${name} arg ${argKind} ${argName} in ${JSON.stringify(json)}`);
            return deserializeArgument(argKind, arg);
        });

        return build(args);
    }

    return deserialize(rootJSON);
}

export function deserializeContext(context: SerializedComponent, env: EvalEnvironment): Context {
    return deserializeEvaluable(context).evalInEnvironment(env, new Data<AppOther, AppEnvironment>(undefined));
}

function evaluateUI(uiComponent: Evaluable, env: EvalEnvironment, ctx: Context): ValueProducer {
    let uiValueProducer: ValueProducer;
    // FIXME: Why do we ever call ui evaluables?  And why is `UIEvaluable` callable?
    if (uiComponent instanceof Callable && !(uiComponent instanceof UIEvaluable)) {
        uiValueProducer = uiComponent.call([], new Data(undefined), env.root);
    } else {
        uiValueProducer = uiComponent.evalInEnvironment(env, ctx);
    }
    return uiValueProducer;
}

interface EvaluableMenuItem {
    readonly title: Evaluable; //                    string
    readonly icon: Evaluable; //                     string
    readonly onTap: Evaluable; //                    Action
    readonly isEnabled: Evaluable | undefined; //    boolean
    readonly isVisible: Evaluable | undefined; //    boolean
    readonly href: Evaluable | undefined; //         string
    readonly style: Evaluable | undefined; //        string
    readonly purpose: Evaluable | undefined; //      string
}

export interface EvaluablePivot {
    readonly componentName: string;
    readonly component: Evaluable | undefined;
    readonly menuItems: ReadonlyArray<EvaluableMenuItem>;
}

let globalIndex = 0;

export function evaluateMenuItem(
    button: EvaluableMenuItem,
    env: EvalEnvironment,
    ctx: Context
): EvaluatedMenuItem | undefined {
    const title = button.title.evalInEnvironment(env, ctx);
    const icon = button.icon.evalInEnvironment(env, ctx);
    const maybeOnTap = button.onTap.evalInEnvironment(env, ctx).current;
    if (isUndefinedish(maybeOnTap)) return undefined;
    return {
        title,
        icon,
        onTap: asAction(maybeOnTap),
        isEnabled: definedMap(button.isEnabled, e => e.evalInEnvironment(env, ctx)),
        isVisible: definedMap(button.isVisible, e => e.evalInEnvironment(env, ctx)),
        href: definedMap(button.href, e => e.evalInEnvironment(env, ctx)),
        style: definedMap(button.style, e => e.evalInEnvironment(env, ctx)),
        purpose: definedMap(button.purpose, e => e.evalInEnvironment(env, ctx)),
    };
}

export class NamedScreenTemplate {
    constructor(
        private readonly _pivots: ReadonlyArray<EvaluablePivot>,
        private readonly _ctxModifier: Evaluable | undefined,
        private readonly _title: Evaluable | undefined,
        private readonly _topBar: Evaluable | undefined,
        private readonly _pivotPicker: Evaluable | undefined,
        private readonly _quotaBannerMessage: Evaluable | undefined,
        private readonly _flags: ReadonlyArray<ScreenFlag>,
        _desc: ScreenDescription | undefined
    ) {}

    public instantiate(
        inputCtx: Context,
        pushedTitle: Evaluable | undefined,
        env: EvalEnvironment,
        additionalScreenFlags: ReadonlySet<ScreenFlag> | undefined,
        screenID: string | undefined,
        itemID: string | undefined
    ): readonly EvaluatedScreen[] {
        const ctxOrEnv = definedMapWithDefault(this._ctxModifier, inputCtx, m => m.evalInEnvironment(env, inputCtx));
        const maybeEnv = asCapturedEvalEnvironment(ctxOrEnv, vp => vp.current);
        const ctx: Context = maybeEnv === undefined ? ctxOrEnv : inputCtx;
        const screenEnv = maybeEnv ?? env;
        const title = definedMap(withDefault(pushedTitle, this._title), t => t.evalInEnvironment(screenEnv, ctx));
        const topBar = definedMap(this._topBar, t => t.evalInEnvironment(screenEnv, ctx));
        const pivotPicker = definedMap(this._pivotPicker, t => t.evalInEnvironment(screenEnv, ctx));
        const quotaBannerMessage =
            definedMap(this._quotaBannerMessage, t => t.evalInEnvironment(screenEnv, ctx)) ??
            new Constant<AppOther, AppEnvironment>(false);

        return this._pivots.map(p => {
            const uiEvaluable = p.component ?? defined(screenEnv.lookupUserDefinedComponent(p.componentName));
            const screen: EvaluatedScreen = {
                title,
                ui: evaluateUI(uiEvaluable, screenEnv, ctx),
                inputCtx,
                ctx,
                name: p.componentName,
                topBar,
                pivotPicker,
                quotaBannerMessage,
                menuItems: mapFilterUndefined(p.menuItems, item => evaluateMenuItem(item, screenEnv, ctx)),
                flags: setUnion(this._flags, additionalScreenFlags ?? []),
                screenId: screenID ?? (globalIndex++).toString(),
                needsRerender: screenID !== undefined,
                itemID,
            };
            return screen;
        });
    }
}

export function deserializeMenuItem(button: SerializedMenuItem): EvaluableMenuItem {
    return {
        title: deserializeEvaluable(button.title),
        icon: deserializeEvaluable(button.icon),
        onTap: deserializeEvaluable(button.onTap),
        isEnabled: definedMap(button.isEnabled, deserializeEvaluable),
        isVisible: definedMap(button.isVisible, deserializeEvaluable),
        href: definedMap(button.href, deserializeEvaluable),
        style: definedMap(button.style, deserializeEvaluable),
        purpose: definedMap(button.purpose, deserializeEvaluable),
    };
}

function deserializeMenuItems(
    menuItems: ReadonlyArray<SerializedMenuItem> | undefined
): ReadonlyArray<EvaluableMenuItem> {
    return definedMapWithDefault(menuItems, [], items => items.map(deserializeMenuItem));
}

function deserializedScreen(serializedApp: SerializedApp, screen: SerializedScreen): NamedScreenTemplate {
    const ctxModifier = definedMap(screen.ctxModifier, deserializeEvaluable);
    const { componentName, pivots } = screen;
    let evaluablePivots: EvaluablePivot[];
    if (componentName !== undefined) {
        evaluablePivots = [
            {
                componentName,
                component: undefined,
                menuItems: deserializeMenuItems(screen.menuItems),
            },
        ];
    } else if (pivots !== undefined) {
        evaluablePivots = pivots.map(p => ({
            componentName: p.componentName,
            component: undefined,
            menuItems: deserializeMenuItems(p.menuItems),
        }));
    } else {
        return panic("Neither component name nor pivots in serialized screen");
    }
    return new NamedScreenTemplate(
        evaluablePivots,
        ctxModifier,
        definedMap(screen.title, deserializeEvaluable),
        definedMap(screen.topBar, deserializeEvaluable),
        definedMap(screen.pivotPicker, deserializeEvaluable),
        definedMap(screen.quotaBannerMessage, deserializeEvaluable),
        definedMapWithDefault(screen.flags, [], x => checkArray(x, checkString) as ScreenFlag[]),
        definedMap(componentName, n => serializedApp.screenDescriptions[n])
    );
}

export interface DeserializedRootScreen {
    readonly pivots: readonly EvaluatedScreen[];
    readonly isVisible: ValueProducer | undefined;
}

function deserializeRootScreen(
    serializedApp: SerializedApp,
    screen: SerializedRootScreen,
    ctx: Context,
    env: EvalEnvironment
): DeserializedRootScreen {
    const title = definedMap(screen.title, deserializeEvaluable);
    if (screen.ctxFromRootContext !== undefined) {
        // The builder analog to this is in `buildScreen` in `BuilderGlideApp.tsx`
        ctx = deserializeEvaluable(screen.ctxFromRootContext).evalInEnvironment(env, ctx);
    }
    const pivots = deserializedScreen(serializedApp, screen).instantiate(
        ctx,
        title,
        env,
        new Set([ScreenFlag.IsTopScreen]),
        undefined,
        undefined
    );
    const isVisible = definedMap(screen.isVisible, deserializeEvaluable)?.evalInEnvironment(env, ctx);

    return { pivots, isVisible };
}

interface DeserializedRoot {
    readonly rootTabs: readonly DeserializedRootScreen[];
    readonly rootContext: Context;
}

function deserializeRoot(serializedApp: SerializedApp, env: EvalEnvironment): DeserializedRoot {
    const { ctx: serializedCtx, tabs } = serializedApp.root;
    const ctx = deserializeContext(serializedCtx, env);
    return { rootTabs: tabs.map(t => deserializeRootScreen(serializedApp, t, ctx, env)), rootContext: ctx };
}

interface DeserializedApp extends DeserializedRoot {
    readonly screens: Map<string, NamedScreenTemplate>;
    readonly functions: Map<string, Evaluable>;

    // indexed by table name, column name
    readonly computedColumns: Map<string, Map<string, Evaluable>>;
}

export function deserializeApp(serializedApp: SerializedApp, appEnvironment: AppEnvironment): DeserializedApp {
    // See the poison pill comment in `SerializedApp.ts`
    const serializedComponents = withDefault(serializedApp.functions, serializedApp.components);
    const functions = new Map<string, Evaluable>();
    if (serializedComponents !== undefined) {
        for (const name of Object.keys(serializedComponents)) {
            functions.set(name, deserializeEvaluable(serializedComponents[name]));
        }
    }

    const serializedScreens = serializedApp.screens;
    const screens = new Map<string, NamedScreenTemplate>();
    for (const name of Object.keys(serializedScreens)) {
        screens.set(name, deserializedScreen(serializedApp, serializedScreens[name]));
    }

    const { rootTabs, rootContext } = deserializeRoot(
        serializedApp,
        new RootEvalEnvironment(appEnvironment, functions)
    );

    const computedColumns = new Map<string, Map<string, Evaluable>>();
    for (const { table, columnName, value } of withDefault(serializedApp.computedColumns, [])) {
        const tableName = getTableRefTableName(table).name;
        let tableEntry = computedColumns.get(tableName);
        if (tableEntry === undefined) {
            tableEntry = new Map();
            computedColumns.set(tableName, tableEntry);
        }

        tableEntry.set(columnName, deserializeEvaluable(value));
    }

    return { rootTabs, rootContext, screens, functions, computedColumns };
}
